import {motion} from "framer-motion";
import fadeEffect from "../animations/fadeEffect";
import Container from "react-bootstrap/Container";
import numeralize from "./numeralize";
import Nav from "react-bootstrap/Nav";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Badge from "react-bootstrap/Badge";
import {LinkContainer} from "react-router-bootstrap";
import {PlayCircle} from "react-bootstrap-icons";
import {useEffect, useState} from "react";
import {BookInterface, SegmentInterface, UrokInterface} from "../pages/Notebook";
import {MediaProp} from "./MediaProp";
import './SegmentViewer.scss'
import {apiCallbacks} from "../App";

interface SegmentViewerProps extends apiCallbacks {
    bookId: number;
    lessonId?: number;
    onSetSegmentId?: (segmentId: number) => void;
    scrollToLesson?: boolean;
    onSegmentData?: (bookData: BookInterface, segmentData: SegmentInterface[]) => void;
    initialBookData?: BookInterface;
    initialSegmentData?: SegmentInterface[];
    segmentNumerals?: boolean;
}

export const SegmentViewer = ({
                                  bookId,
                                  lessonId,
                                  onSetSegmentId,
                                  scrollToLesson,
                                  onSegmentData,
                                  initialBookData, initialSegmentData,
                                  segmentNumerals,
                                  requireAuthCallback, authCheckCatchCallback
                              }: SegmentViewerProps) => {
    const [bookData, setBookData] = useState<BookInterface | undefined>(initialBookData);
    const [segmentData, setSegmentData] = useState<SegmentInterface[] | undefined>(initialSegmentData);
    const [segmentId, setSegmentId] = useState<number>(parseInt(window.location.hash.slice(1)));
    const mediaProps = MediaProp.Query();

    function triggerLessonScroll() {
        const activeLessons = document.getElementsByClassName("active-lesson");
        if (activeLessons.length === 0) return
        activeLessons[0].scrollIntoView({behavior: 'smooth', block: 'center'})
    }

    function handleOnSetSegmentId(segmentId: number) {
        setSegmentId(segmentId);
        if (onSetSegmentId) onSetSegmentId(segmentId);
    }

    function extractLessonLink(urok: UrokInterface, lesson_id: number) {
        const lesson_item = urok.lessons[lesson_id];
        const lesson_link = `/category/${bookData?.categoryId}/${bookData?.subId}/${urok.id}/${lesson_item.id}`;
        return {
            to: lesson_link
        }
    }
    const handleHashChange = () => {
        handleOnSetSegmentId(parseInt(window.location.hash.slice(1)))
    };

    useEffect(() => {

        window.addEventListener('hashchange', handleHashChange);

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    });



    useEffect(() => {
        if (bookData !== undefined && segmentData !== undefined) return
        fetch(`${process.env.REACT_APP_API}/books/${bookId}/`, {credentials: "include"})
            .then(requireAuthCallback)
            .then((data) => {
                setBookData(data)
            })
            .then(() => {
                if (segmentId) {
                    document.getElementById(segmentId.toString())?.scrollIntoView({
                        behavior: 'smooth'
                    })
                }
            })
            .then(() => {
                fetch(`${process.env.REACT_APP_API}/categories/segments/${bookId}/`, {credentials: "include"})
                    .then(requireAuthCallback)
                    .then((data) => {
                        setSegmentData(data)
                    })
                    .then(() => {
                        if (!scrollToLesson) return
                        setTimeout(() => triggerLessonScroll(), 100)
                    })
                    .catch(authCheckCatchCallback);
            })
            .catch(authCheckCatchCallback);
    }, [authCheckCatchCallback, bookData, bookId, requireAuthCallback, scrollToLesson, segmentData, segmentId]);

    useEffect(() => {
        if (onSegmentData !== undefined &&
            bookData !== undefined &&
            segmentData !== undefined) onSegmentData(bookData, segmentData)
    }, [bookData, onSegmentData, segmentData]);

    if (!bookData) return (<></>);

    return (<>
            {bookData.segments.map((segment, index) => (
                <motion.div {...fadeEffect} key={`segment-${index}`}>
                    <Container fluid="xxl" className="rounded-2 segment-item" id={segment.id.toString()}>
                        <Container className="border rounded-2 border-bottom-0 shadow-sm rounded-bottom-0"
                                   fluid="lg">
                            {(segmentNumerals || segmentNumerals === undefined) &&
                                <p className="px-3 mt-2 mb-1">{numeralize(index + 1)} Раздел</p>
                            }
                            <h4 className="px-3 mb-0 pb-2">{segment.title}</h4>
                        </Container>
                        {segmentData !== undefined &&
                            <motion.div {...fadeEffect} key="section-data">
                                <Nav
                                    className="justify-content-center container-lg mb-5 p-0 border border-top-0 rounded-2 rounded-top-0 bg-gray w-100">
                                    {segmentData[index].uroks.map((urok) => (
                                        <Nav.Item className="w-100 lessons-nav-item">
                                            <Row
                                                className={`w-100 lesson-item one m-0 border-top ${urok.lessons.length > 1 ? 'shadow-sm many' : urok.lessons[0].id === lessonId ? 'active active-lesson' : ''}`}>
                                                <Col className="align-self-center">
                                                    {((urok.lessons.length === 1 && !mediaProps.mobile) || urok.lessons.length > 1) &&
                                                        <h5 className="m-0">{urok.title}</h5>
                                                    }
                                                    {(urok.lessons.length === 1 && mediaProps.mobile) &&
                                                        <Row>
                                                            <Col className="align-self-center">
                                                                <h5 className="m-0">{urok.title}</h5>
                                                                <Badge className="my-2"
                                                                       bg="secondary">{urok.page}</Badge>
                                                            </Col>
                                                            <Col xs="auto" className="align-self-center"><LinkContainer
                                                                {...extractLessonLink(urok, 0)}><Nav.Link
                                                                className="ms-1 p-0"><PlayCircle
                                                                size={30}/></Nav.Link></LinkContainer></Col>
                                                        </Row>
                                                    }
                                                </Col>
                                                {!(urok.lessons.length === 1 && mediaProps.mobile) &&
                                                    <Col xs={mediaProps.mobile ? 2 : 3}
                                                         className="p-0 lesson-nav me-3 h-100 align-self-center">
                                                        <Row
                                                            className="align-items-center d-flex justify-content-end align-content-center h-100">
                                                            <Col xs={6} className="m-1 p-1 lesson-badge"><Badge
                                                                bg="secondary">{urok.page}</Badge></Col>
                                                            {urok.lessons.length === 1 &&
                                                                <Col xs={6}
                                                                     className="lesson-badge m-1 p-1"><LinkContainer
                                                                    {...extractLessonLink(urok, 0)}><Nav.Link
                                                                    className="p-0"><PlayCircle
                                                                    size={30}/></Nav.Link></LinkContainer></Col>
                                                            }
                                                            {(urok.lessons.length > 1) &&
                                                                <Col xs={6} className="m-1 p-1 lesson-badge"><Badge
                                                                    bg="warning">{urok.lessons.length} части</Badge></Col>
                                                            }
                                                        </Row>
                                                    </Col>
                                                }

                                                {urok.lessons.length > 1 &&
                                                    <Nav className="w-100 ms-3 p-0 flex-column">
                                                        {urok.lessons.map((lesson, lesson_index) => (
                                                            <Nav.Item
                                                                className={`d-flex flex-row flex-grow-1 p-1 lesson-item many border-top ${lesson.id === lessonId ? 'active active-lesson' : ''}`}>
                                                                <div
                                                                    className="align-content-center d-flex lesson-part">
                                                                    <h6 className="m-auto text-center">Част {lesson.part}</h6>
                                                                </div>
                                                                <div className="p-0 lesson-nav">
                                                                    <Row
                                                                        className="align-items-center d-flex justify-content-end align-content-center h-100">
                                                                        <Col><LinkContainer
                                                                            {...extractLessonLink(urok, lesson_index)}><Nav.Link
                                                                            className="p-0"><PlayCircle
                                                                            size={30}/></Nav.Link></LinkContainer></Col>
                                                                    </Row>
                                                                </div>
                                                            </Nav.Item>
                                                        ))}
                                                    </Nav>
                                                }
                                            </Row>
                                        </Nav.Item>
                                    ))}
                                </Nav>
                            </motion.div>
                        }
                    </Container>
                </motion.div>
            ))
            }
        </>
    );
};